import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type {
  IndustryCard,
  PackageConfigsIndex,
  PackageSelectionState,
  PricingGridsResponse,
  ProductCard,
} from "@itaas/pep-contracts";
import {
  getIndustries,
  getPackageConfigs,
  getPricingGrids,
  getProducts,
  submitConfigureSubmission,
} from "../api/pep-api";
import { trackEvent } from "../analytics/pep-analytics";
import { SiteLayout } from "../components/layout/SiteLayout";
import { hubPageJsonLd, SeoHead } from "../components/seo/SeoHead";
import { usePepI18n } from "../i18n/usePepI18n";
import { getAffiliateCodeForLead } from "../utils/affiliate-attribution";
import { dimensionHint, dimensionLabel, dimensionMinQty, lowQuantityMessage } from "../utils/billing-dimensions";
import {
  buildInitialCommercialSelection,
  clearStoredDraft,
  commercialEstimate,
  configureContexts,
  portfolioBadgeLabel,
  resolveContextFromQuery,
  resolveDraftFromStorage,
  resolveOrganizationDimensions,
  resolvePrimarySolution,
  resolveReturnIntent,
  resolveVisibleCapabilities,
  safeStep,
  setCommercialModuleSelected,
  setCommercialQuantity,
  storeDraft,
  visibleProductsMap,
  type CommercialEstimateView,
  type ConfigureContextDefinition,
  type ConfigureDraft,
  type ConfigureStep,
} from "../configure/configure-model";
import {
  resolveConfigureFinalCtaMode,
  toConfigureFinalCtaView,
} from "../configure/configure-final-cta";
import {
  toConfigureSuccessView,
  type ConfigureSuccessView,
} from "../configure/configure-success";

const DRAFT_KEY = "pep.configure.draft.v2";
const DRAFT_VERSION = "2026-07-29";
const IDEMPOTENCY_KEY = "pep.configure.idempotency.v1";

function ensureIdempotencyKey(): string {
  try {
    const existing = sessionStorage.getItem(IDEMPOTENCY_KEY);
    if (existing && existing.length >= 8) return existing;
    const generated =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `cfg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(IDEMPOTENCY_KEY, generated);
    return generated;
  } catch {
    return `cfg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function clearIdempotencyKey() {
  try {
    sessionStorage.removeItem(IDEMPOTENCY_KEY);
  } catch {
    // ignore
  }
}

function GraduationIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 3 2 8l10 5 8-4v5h2V8L12 3Zm-6 8.5V16c0 1.8 3.1 3.5 6 3.5s6-1.7 6-3.5v-4.5l-6 3-6-3Z"
        fill="currentColor"
      />
    </svg>
  );
}

function ContextIcon({ kind }: { kind: ConfigureContextDefinition["icon"] }) {
  if (kind === "education") return <GraduationIcon />;
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M4 5h6v2H4V5Zm0 6h10v2H4v-2Zm0 6h14v2H4v-2Zm13-9 3 3-3 3v-2h-4v-2h4V8Z"
        fill="currentColor"
      />
    </svg>
  );
}

function Stepper({
  step,
  locale,
}: {
  step: ConfigureStep;
  locale: "fr" | "en";
}) {
  const labels =
    locale === "en"
      ? [
          ["Your needs", "Completed"],
          ["Your solution", "In progress"],
          ["Your organization", "Upcoming"],
          ["Your configuration", "Upcoming"],
        ]
      : [
          ["Votre besoin", "Terminé"],
          ["Votre solution", "En cours"],
          ["Votre organisation", "À venir"],
          ["Votre configuration", "À venir"],
        ];

  return (
    <ol className="pep-configure-stepper list-unstyled mb-0" aria-label={locale === "en" ? "Configure steps" : "Étapes de configuration"}>
      {labels.map(([title], index) => {
        const number = index + 1;
        const state = number < step ? "done" : number === step ? "active" : "upcoming";
        return (
          <li key={title} className={`pep-configure-stepper__item is-${state}`}>
            <div className="pep-configure-stepper__badge" aria-hidden="true">
              {state === "done" ? "✓" : number}
            </div>
            <div className="pep-configure-stepper__text">
              <span className="pep-configure-stepper__title">{title}</span>
              <span className="pep-configure-stepper__subtitle">
                {number < step
                  ? locale === "en"
                    ? "Completed"
                    : "Terminé"
                  : number === step
                    ? locale === "en"
                      ? "In progress"
                      : "En cours"
                    : locale === "en"
                      ? "Upcoming"
                      : "À venir"}
              </span>
            </div>
            {number < labels.length ? <span className="pep-configure-stepper__line" aria-hidden="true" /> : null}
          </li>
        );
      })}
    </ol>
  );
}

function statusLabel(role: "included" | "recommended" | "optional", locale: "fr" | "en") {
  if (role === "included") return locale === "en" ? "Included" : "Inclus";
  if (role === "recommended") return locale === "en" ? "Recommended" : "Recommandé";
  return locale === "en" ? "Optional" : "Optionnel";
}

export function ConfigurePage() {
  const { locale } = usePepI18n();
  const [params] = useSearchParams();
  const [products, setProducts] = useState<ProductCard[]>([]);
  const [industries, setIndustries] = useState<IndustryCard[]>([]);
  const [packageIndex, setPackageIndex] = useState<PackageConfigsIndex | null>(null);
  const [pricingGrids, setPricingGrids] = useState<PricingGridsResponse | null>(null);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [step, setStep] = useState<ConfigureStep>(1);
  const [contextId, setContextId] = useState<ConfigureContextDefinition["id"] | null>(null);
  const [selection, setSelection] = useState<PackageSelectionState | null>(null);
  const [unknownQuantityKeys, setUnknownQuantityKeys] = useState<Set<string>>(() => new Set());
  const [draft, setDraft] = useState<ConfigureDraft | null>(null);
  const [draftDecisionMade, setDraftDecisionMade] = useState(false);

  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [sent, setSent] = useState(false);
  const [successView, setSuccessView] = useState<ConfigureSuccessView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitPending, setSubmitPending] = useState(false);

  const returnTo = resolveReturnIntent(params);
  const productsBySlug = useMemo(() => visibleProductsMap(products), [products]);
  const contexts = useMemo(() => configureContexts(), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadingError(null);

    void Promise.all([getProducts(), getIndustries(), getPackageConfigs(), getPricingGrids()])
      .then(([productList, industryList, pkgIndex, grids]) => {
        if (!active) return;
        setProducts(productList);
        setIndustries(industryList);
        setPackageIndex(pkgIndex);
        setPricingGrids(grids);
      })
      .catch((cause) => {
        if (!active) return;
        console.error(cause);
        setLoadingError(
          locale === "en"
            ? "Unable to load the configuration assistant right now."
            : "Impossible de charger l’assistant de configuration pour le moment.",
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [locale]);

  useEffect(() => {
    if (loading || !packageIndex) return;
    const stored = resolveDraftFromStorage(DRAFT_KEY);
    const resolvedContext = resolveContextFromQuery(params, packageIndex, industries);

    if (resolvedContext) {
      const pkg = packageIndex.packages.find((entry) => entry.slug === resolvedContext.packageSlug) ?? null;
      if (!pkg) return;
      setContextId(resolvedContext.id);
      setSelection(buildInitialCommercialSelection(pkg, productsBySlug));
      setUnknownQuantityKeys(new Set());
      setStep(resolvedContext.id ? 2 : 1);
      setDraftDecisionMade(true);
      return;
    }

    if (stored?.version === DRAFT_VERSION && !draftDecisionMade) {
      setDraft(stored);
      return;
    }

    setDraftDecisionMade(true);
  }, [draftDecisionMade, industries, loading, packageIndex, params, productsBySlug]);

  useEffect(() => {
    if (!draft || !packageIndex || !draftDecisionMade || contextId || selection) return;
    const restoredContext = contexts.find((entry) => entry.id === draft.contextId) ?? null;
    if (!restoredContext) return;
    const pkg = packageIndex.packages.find((entry) => entry.slug === draft.packageSlug);
    if (!pkg) return;
    const base = buildInitialCommercialSelection(pkg, productsBySlug);
    setContextId(restoredContext.id);
    setSelection({
      ...base,
      selectedModules: draft.selectedModules.length > 0 ? draft.selectedModules : base.selectedModules,
      quantities: { ...base.quantities, ...draft.quantities },
      seatCount: base.seatCount,
    });
    setUnknownQuantityKeys(new Set(draft.unknownQuantityKeys));
    setStep(safeStep(draft.step));
    setDraftDecisionMade(true);
  }, [contextId, contexts, draft, draftDecisionMade, packageIndex, productsBySlug, selection]);

  const context = useMemo(
    () => contexts.find((entry) => entry.id === contextId) ?? null,
    [contextId, contexts],
  );
  const pkg = useMemo(
    () => (packageIndex && selection ? packageIndex.packages.find((entry) => entry.slug === selection.packageSlug) ?? null : null),
    [packageIndex, selection],
  );
  const primary = useMemo(
    () => (pkg ? resolvePrimarySolution(pkg, productsBySlug, locale) : null),
    [locale, pkg, productsBySlug],
  );
  const capabilities = useMemo(
    () => (pkg && selection ? resolveVisibleCapabilities(pkg, selection, productsBySlug, locale) : []),
    [locale, pkg, productsBySlug, selection],
  );
  const dimensions = useMemo(
    () => (pkg && selection && pricingGrids ? resolveOrganizationDimensions(pkg, selection, pricingGrids) : []),
    [pkg, pricingGrids, selection],
  );
  const estimate = useMemo<CommercialEstimateView | null>(
    () =>
      pkg && selection && pricingGrids && packageIndex
        ? commercialEstimate(pkg, selection, packageIndex, pricingGrids, productsBySlug, unknownQuantityKeys, locale)
        : null,
    [locale, packageIndex, pkg, pricingGrids, productsBySlug, selection, unknownQuantityKeys],
  );
  const selectedCapabilityTitles = capabilities.filter((capability) => capability.selected).map((capability) => capability.title);
  const finalCta = useMemo(
    () => toConfigureFinalCtaView(primary?.product ?? null, locale),
    [locale, primary],
  );

  useEffect(() => {
    if (!selection || !contextId) return;
    const nextDraft: ConfigureDraft = {
      version: DRAFT_VERSION,
      step,
      contextId,
      packageSlug: selection.packageSlug,
      selectedModules: selection.selectedModules,
      quantities: selection.quantities,
      unknownQuantityKeys: [...unknownQuantityKeys],
      returnTo,
    };
    storeDraft(DRAFT_KEY, nextDraft);
  }, [contextId, returnTo, selection, step, unknownQuantityKeys]);

  useEffect(() => {
    void trackEvent("configure_started", {
      intent: returnTo,
      has_context_param: Boolean(params.get("industry") || params.get("product") || params.get("package")),
    });
  }, [params, returnTo]);

  useEffect(() => {
    if (!context) return;
    void trackEvent("configure_context_selected", {
      context: context.id,
      industry: context.industrySlug,
      package: context.packageSlug,
    });
  }, [context]);

  useEffect(() => {
    if (step === 2 && context && pkg) {
      void trackEvent("configure_solution_viewed", {
        context: context.id,
        package: pkg.slug,
        product: pkg.primaryProduct,
      });
    }
  }, [context, pkg, step]);

  const seoTitle = locale === "en" ? "Configure your ITaaS solution" : "Configurez votre solution ITaaS";
  const seoDescription =
    locale === "en"
      ? "Build the ITaaS solution that fits your organization, step by step."
      : "Construisez la solution ITaaS adaptée à votre organisation, étape par étape.";

  function selectContext(nextContext: ConfigureContextDefinition) {
    if (!packageIndex) return;
    const nextPackage = packageIndex.packages.find((entry) => entry.slug === nextContext.packageSlug);
    if (!nextPackage) return;

    setContextId(nextContext.id);
    setSelection(buildInitialCommercialSelection(nextPackage, productsBySlug));
    setUnknownQuantityKeys(new Set());
    setStep(2);
    setDraft(null);
    setDraftDecisionMade(true);
  }

  function changeStep(nextStep: ConfigureStep) {
    setStep(nextStep);
    if (context && pkg) {
      void trackEvent("configure_step_completed", {
        context: context.id,
        package: pkg.slug,
        step: nextStep - 1,
      });
    }
  }

  function toggleCapability(slug: string, selectedValue: boolean) {
    if (!selection || !pkg) return;
    setSelection(setCommercialModuleSelected(selection, pkg, slug, selectedValue));
    void trackEvent("configure_addon_selected", {
      context: context?.id,
      package: pkg.slug,
      addon: slug,
      selected: selectedValue,
    });
  }

  function updateQuantity(key: string, value: number) {
    if (!selection) return;
    setSelection(setCommercialQuantity(selection, key, value));
  }

  function toggleUnknownQuantity(key: string, checked: boolean) {
    setUnknownQuantityKeys((current) => {
      const next = new Set(current);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  function resetConfiguration() {
    clearStoredDraft(DRAFT_KEY);
    clearIdempotencyKey();
    setContextId(null);
    setSelection(null);
    setUnknownQuantityKeys(new Set());
    setDraft(null);
    setSent(false);
    setSuccessView(null);
    setError(null);
    setStep(1);
    setDraftDecisionMade(true);
  }

  async function sendSubmission() {
    if (!selection || !pkg || !context || !estimate || !primary) return;
    setError(null);
    setSubmitPending(true);

    const capabilitySlugs = capabilities
      .filter((entry) => entry.selected && entry.slug !== primary.slug)
      .map((entry) => entry.slug);

    void trackEvent("configure_submit_started", {
      context: context.id,
      package: pkg.slug,
      intent: returnTo,
    });

    try {
      const result = await submitConfigureSubmission({
        idempotencyKey: ensureIdempotencyKey(),
        contextId: context.id,
        packageSlug: selection.packageSlug,
        selectedCapabilities: capabilitySlugs,
        organization: { name: company.trim() },
        contact: {
          name: (name.trim() || company).trim(),
          email: email.trim(),
          phone: phone.trim(),
        },
        locale,
        quantities: selection.quantities,
        unknownQuantityKeys: [...unknownQuantityKeys],
        moduleProfiles: selection.moduleProfiles,
        affiliateCode: getAffiliateCodeForLead(),
        // Single chain: CTA mode of primary product drives intent (not URL alone).
        intent: resolveConfigureFinalCtaMode(primary) === "self_service" ? "trial" : "devis",
        source: "pep-configure",
      });

      const view = toConfigureSuccessView(result);
      const accepted = view !== null;

      if (accepted && view) {
        void trackEvent("configure_submit_succeeded", {
          context: context.id,
          package: pkg.slug,
          intent: returnTo,
          reference: result.reference,
          successKind: view.kind,
          businessOutcome: result.activation?.businessOutcome,
        });
        clearStoredDraft(DRAFT_KEY);
        clearIdempotencyKey();
        setSuccessView(view);
        setSent(true);
        return;
      }

      void trackEvent("configure_submit_failed", {
        context: context.id,
        package: pkg.slug,
        intent: returnTo,
        errorCode: result.errorCode ?? "NOTIFICATION_FAILED",
        reference: result.reference,
      });
      setError(
        locale === "en"
          ? "Your request could not be sent. Your information was kept so you can retry without restarting the configuration."
          : "Votre demande n’a pas pu être envoyée. Vos informations ont été conservées afin que vous puissiez réessayer sans recommencer votre configuration.",
      );
    } catch {
      void trackEvent("configure_submit_failed", {
        context: context.id,
        package: pkg.slug,
        intent: returnTo,
        errorCode: "REQUEST_FAILED",
      });
      setError(
        locale === "en"
          ? "Your request could not be sent. Your information was kept so you can retry without restarting the configuration."
          : "Votre demande n’a pas pu être envoyée. Vos informations ont été conservées afin que vous puissiez réessayer sans recommencer votre configuration.",
      );
    } finally {
      setSubmitPending(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void sendSubmission();
  }

  return (
    <SiteLayout>
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        canonicalPath="/configure"
        locale={locale}
        jsonLd={hubPageJsonLd({
          canonicalPath: "/configure",
          name: seoTitle,
          description: seoDescription,
          serviceName:
            locale === "en" ? "ITaaS Suite solution configurator" : "Configurateur de solution ITaaS Suite",
          serviceType: "SoftwareConfiguration",
        })}
      />
      <div className="container py-4 py-lg-5">
        <section className="pep-configure-shell">
          <header className="pep-configure-hero">
            <div className="pep-configure-hero__intro">
              <span className="pep-configure-hero__icon">
                <ContextIcon kind={context?.icon ?? "education"} />
              </span>
              <div>
                <h1 className="pep-configure-hero__title">
                  {locale === "en" ? "Build your ITaaS solution" : "Construisez votre solution ITaaS"}
                </h1>
                <p className="pep-configure-hero__text">
                  {locale === "en"
                    ? "Tell us what you want to manage. We will help you shape a solution that fits your organization."
                    : "Dites-nous ce que vous souhaitez gérer. Nous vous aidons à composer une solution adaptée à votre organisation."}
                </p>
              </div>
            </div>

            {context ? (
              <div className="pep-configure-hero__context">
                <span className="pep-configure-hero__context-icon">
                  <ContextIcon kind={context.icon} />
                </span>
                <div className="flex-grow-1">
                  <span className="pep-configure-hero__context-label">
                    {locale === "en" ? "Selected context" : "Contexte sélectionné"}
                  </span>
                  <strong className="pep-configure-hero__context-title">
                    {locale === "en" ? context.titleEn : context.titleFr}
                  </strong>
                </div>
                <button type="button" className="btn btn-outline-primary" onClick={resetConfiguration}>
                  {locale === "en" ? "Modify" : "Modifier"}
                </button>
              </div>
            ) : null}
          </header>

          {draft && !context ? (
            <div className="alert alert-info d-flex flex-column flex-lg-row align-items-lg-center gap-3">
              <div className="flex-grow-1">
                <strong>{locale === "en" ? "Resume your configuration" : "Reprendre votre configuration"}</strong>
                <div className="small">
                  {locale === "en"
                    ? "A saved draft is available on this device."
                    : "Un brouillon enregistré est disponible sur cet appareil."}
                </div>
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setDraftDecisionMade(true);
                    const restored = resolveDraftFromStorage(DRAFT_KEY);
                    if (restored) setDraft(restored);
                  }}
                >
                  {locale === "en" ? "Resume" : "Reprendre"}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    clearStoredDraft(DRAFT_KEY);
                    setDraft(null);
                    setDraftDecisionMade(true);
                  }}
                >
                  {locale === "en" ? "Start over" : "Recommencer"}
                </button>
              </div>
            </div>
          ) : null}

          <div className="pep-configure-stepper-wrap">
            <Stepper step={context ? step : 1} locale={locale} />
          </div>
        </section>

        {loading ? (
          <div className="card border-0 shadow-sm p-4 mt-4">
            <p className="mb-0 text-secondary">
              {locale === "en" ? "Loading the configuration assistant…" : "Chargement de l’assistant de configuration…"}
            </p>
          </div>
        ) : loadingError ? (
          <div className="alert alert-danger mt-4" role="alert">
            {loadingError}
          </div>
        ) : sent && successView ? (
          <section className="pep-configure-success mt-4">
            <div className="pep-configure-success__badge" aria-hidden="true">
              ✓
            </div>
            {successView.kind === "environment_ready" ? (
              <>
                <h2>
                  {locale === "en" ? "Your environment is ready" : "Votre environnement est prêt"}
                </h2>
                <p className="text-secondary mb-2">
                  {locale === "en"
                    ? "Provisioning completed for your organization. You can open your workspace now."
                    : "Le provisionnement de votre organisation est terminé. Vous pouvez ouvrir votre espace maintenant."}
                </p>
                <p className="text-secondary mb-4">
                  {locale === "en"
                    ? "Your credentials have been sent to your email address."
                    : "Vos identifiants ont été envoyés à votre adresse e-mail."}
                </p>
              </>
            ) : (
              <>
                <h2>{locale === "en" ? "Your request has been sent" : "Votre demande a été envoyée"}</h2>
                <p className="text-secondary mb-4">
                  {locale === "en"
                    ? "Our team will review your configuration and come back with the right next step."
                    : "Notre équipe va relire votre configuration et revenir vers vous avec la prochaine étape adaptée."}
                </p>
              </>
            )}

            <p className="text-secondary small mb-3">
              {locale === "en" ? "Reference" : "Référence"}: {successView.reference}
            </p>

            {successView.kind === "environment_ready" && successView.activation ? (
              <div className="pep-configure-summary-card mb-3">
                <h3>
                  {locale === "en" ? "Provisioning summary" : "Récapitulatif de provisionnement"}
                </h3>
                <ul className="list-unstyled mb-0">
                  <li>
                    <strong>{locale === "en" ? "Organization" : "Organisation"}:</strong>{" "}
                    {successView.recap.organizationName}
                  </li>
                  <li>
                    <strong>{locale === "en" ? "Status" : "Statut"}:</strong>{" "}
                    {successView.activation.businessOutcome}
                    {successView.activation.technicalOutcome
                      ? ` · ${successView.activation.technicalOutcome}`
                      : ""}
                  </li>
                  {successView.lifecycleStatus ? (
                    <li>
                      <strong>Lifecycle:</strong> {successView.lifecycleStatus}
                    </li>
                  ) : null}
                  {successView.activation.tenantSlug ? (
                    <li>
                      <strong>Tenant:</strong> {successView.activation.tenantSlug}
                    </li>
                  ) : null}
                  {successView.activation.adminEmail ? (
                    <li>
                      <strong>{locale === "en" ? "Sign-in email" : "Email de connexion"}:</strong>{" "}
                      {successView.activation.adminEmail}
                    </li>
                  ) : null}
                  {successView.activation.provisionedModules &&
                  successView.activation.provisionedModules.length > 0 ? (
                    <li>
                      <strong>
                        {locale === "en" ? "Provisioned modules" : "Modules provisionnés"}:
                      </strong>{" "}
                      {successView.activation.provisionedModules.join(", ")}
                    </li>
                  ) : null}
                  {successView.activation.assistedModules &&
                  successView.activation.assistedModules.length > 0 ? (
                    <li>
                      <strong>
                        {locale === "en" ? "Assisted modules" : "Modules assistés"}:
                      </strong>{" "}
                      {successView.activation.assistedModules.join(", ")}
                    </li>
                  ) : null}
                  {successView.activation.loginUrl ? (
                    <li>
                      <strong>{locale === "en" ? "Login URL" : "URL de connexion"}:</strong>{" "}
                      <span className="text-break">{successView.activation.loginUrl}</span>
                    </li>
                  ) : null}
                  <li>
                    <strong>{locale === "en" ? "Notification" : "Notification"}:</strong>{" "}
                    {successView.notificationStatus}
                  </li>
                  {successView.activation.message ? (
                    <li>
                      <strong>{locale === "en" ? "Message" : "Message"}:</strong>{" "}
                      {successView.activation.message}
                    </li>
                  ) : null}
                </ul>
              </div>
            ) : null}

            <div className="pep-configure-summary-card pep-configure-summary-card--secondary">
              <h3>
                {locale === "en" ? "Configuration summary" : "Récapitulatif de configuration"}
              </h3>
              <ul className="list-unstyled mb-0">
                <li>
                  <strong>{locale === "en" ? "Context" : "Contexte"}:</strong>{" "}
                  {successView.recap.contextLabel}
                </li>
                <li>
                  <strong>{locale === "en" ? "Main solution" : "Solution principale"}:</strong>{" "}
                  {successView.recap.primarySolutionLabel}
                </li>
                {successView.recap.capabilityLabels.length > 0 ? (
                  <li>
                    <strong>
                      {locale === "en" ? "Complementary capabilities" : "Capacités complémentaires"}:
                    </strong>{" "}
                    {successView.recap.capabilityLabels.join(", ")}
                  </li>
                ) : null}
                <li>
                  <strong>{locale === "en" ? "Commercial result" : "Résultat commercial"}:</strong>{" "}
                  {successView.recap.commercialResultLabel}
                </li>
                {successView.kind !== "environment_ready" ? (
                  <li>
                    <strong>{locale === "en" ? "Notification" : "Notification"}:</strong>{" "}
                    {successView.notificationStatus}
                  </li>
                ) : null}
              </ul>
            </div>

            {successView.kind === "environment_ready" ? (
              <div className="pep-configure-success__next">
                <h3>{locale === "en" ? "Next step" : "Prochaine étape"}</h3>
                <p className="mb-0">
                  {successView.activation?.loginUrl
                    ? locale === "en"
                      ? successView.notificationStatus === "sent"
                        ? "Open your workspace below. Sign-in details are also available via the Welcome email when delivery status is sent."
                        : "Open your workspace with the button below."
                      : successView.notificationStatus === "sent"
                        ? "Ouvrez votre espace ci-dessous. Les identifiants sont aussi disponibles via l’e-mail Welcome lorsque le statut de notification est sent."
                        : "Ouvrez votre espace avec le bouton ci-dessous."
                    : locale === "en"
                      ? "Your environment is provisioned. Follow the commercial next step if a login link is not available yet."
                      : "Votre environnement est provisionné. Suivez l’étape commerciale si le lien de connexion n’est pas encore disponible."}
                </p>
              </div>
            ) : (
              <div className="pep-configure-success__next">
                <h3>{locale === "en" ? "Next step" : "Prochaine étape"}</h3>
                <p className="mb-0">
                  {returnTo === "trial"
                    ? locale === "en"
                      ? "We will confirm the best trial path for your context and selected scope."
                      : "Nous confirmerons le meilleur parcours d’essai selon votre contexte et le périmètre sélectionné."
                    : locale === "en"
                      ? "We will contact you with the right commercial follow-up for your scope."
                      : "Nous vous recontacterons avec le suivi commercial adapté à votre périmètre."}
                </p>
              </div>
            )}

            <div className="d-flex flex-column flex-md-row gap-3 mt-4">
              {successView.activation?.loginUrl ? (
                <a
                  href={successView.activation.loginUrl}
                  className="btn btn-primary btn-lg"
                  rel="noopener noreferrer"
                >
                  {locale === "en" ? "Access my environment" : "Accéder à mon environnement"}
                </a>
              ) : (
                <Link to={returnTo === "trial" ? "/configure" : "/contact"} className="btn btn-primary btn-lg">
                  {returnTo === "trial"
                    ? locale === "en"
                      ? "Create my environment"
                      : "Créer mon environnement"
                    : locale === "en"
                      ? "Talk to sales"
                      : "Parler aux ventes"}
                </Link>
              )}
              <Link to="/products" className="btn btn-outline-secondary btn-lg">
                {locale === "en" ? "Browse products" : "Découvrir les produits"}
              </Link>
            </div>
          </section>
        ) : (
          <div className="mt-4">
            {!context || !selection || !pkg || !pricingGrids ? (
              <section className="pep-configure-panel">
                <div className="pep-configure-panel__header">
                  <h2>{locale === "en" ? "Your needs" : "Votre besoin"}</h2>
                  <p>
                    {locale === "en"
                      ? "Choose the business context that best matches your organization."
                      : "Choisissez le contexte métier qui correspond le mieux à votre organisation."}
                  </p>
                </div>
                <div className="pep-configure-context-grid">
                  {contexts.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      className="pep-configure-context-card"
                      onClick={() => selectContext(entry)}
                    >
                      <span className="pep-configure-context-card__icon">
                        <ContextIcon kind={entry.icon} />
                      </span>
                      <span className="pep-configure-context-card__title">
                        {locale === "en" ? entry.titleEn : entry.titleFr}
                      </span>
                      <span className="pep-configure-context-card__text">
                        {locale === "en" ? entry.descriptionEn : entry.descriptionFr}
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            ) : step === 2 ? (
              <section className="pep-configure-panel">
                <div className="pep-configure-panel__header">
                  <h2>{locale === "en" ? "Your recommended solution" : "Votre solution recommandée"}</h2>
                  <p>
                    {locale === "en"
                      ? `Based on your ${context.titleEn} context, here is a commercial solution you can refine.`
                      : `Selon votre contexte ${context.titleFr}, voici une solution commerciale que vous pouvez affiner.`}
                  </p>
                </div>

                <div className="pep-configure-solution-grid">
                  <article className="pep-configure-primary-card">
                    <div className="pep-configure-primary-card__header">
                      <span className="pep-configure-primary-card__icon">
                        <GraduationIcon />
                      </span>
                      <div className="flex-grow-1">
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <h3 className="mb-0">{primary?.title}</h3>
                          <span className="pep-configure-status-badge is-included">
                            {locale === "en" ? "Included" : "Inclus"}
                          </span>
                        </div>
                        <p className="mb-0 text-secondary">
                          {primary?.tagline || primary?.description}
                        </p>
                      </div>
                    </div>

                    <div className="pep-configure-primary-card__features">
                      {(primary?.features ?? []).map((feature) => (
                        <div key={feature} className="pep-configure-primary-card__feature">
                          <span className="pep-configure-check" aria-hidden="true">
                            ✓
                          </span>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="pep-configure-primary-card__footer">
                      <Link to={`/products/${primary?.slug ?? pkg.primaryProduct}`} className="pep-configure-inline-link">
                        {locale === "en"
                          ? `See ${primary?.title ?? "this solution"} in detail`
                          : `Voir le détail de ${primary?.title ?? "cette solution"}`}
                      </Link>
                    </div>
                  </article>

                  <aside className="pep-configure-capabilities">
                    <div className="pep-configure-capabilities__hint">
                      {locale === "en"
                        ? "Only commercially available solutions are shown here."
                        : "Seules les solutions disponibles sont affichées ici."}
                    </div>
                    <h3>{locale === "en" ? "Complementary capabilities" : "Capacités complémentaires"}</h3>
                    <p className="text-secondary">
                      {locale === "en"
                        ? "Add capabilities according to your business needs."
                        : "Ajoutez des capacités selon vos besoins métier."}
                    </p>

                    <div className="d-flex flex-column gap-3">
                      {capabilities.map((capability) => {
                        const badge = portfolioBadgeLabel(capability.status, locale);
                        return (
                          <div
                            key={capability.slug}
                            className={`pep-configure-capability-card ${capability.selected ? "is-selected" : ""}`}
                          >
                            <div className="pep-configure-capability-card__body">
                              <div className="pep-configure-capability-card__copy">
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                  <h4>{capability.title}</h4>
                                  <span className={`pep-configure-status-badge is-${capability.commercialRole}`}>
                                    {statusLabel(capability.commercialRole, locale)}
                                  </span>
                                  {badge ? <span className="pep-configure-status-badge is-limited">{badge}</span> : null}
                                </div>
                                <p className="pep-configure-capability-card__subtitle">{capability.description}</p>
                                <div className="pep-configure-capability-card__meta">
                                  {capability.subtitle}
                                </div>
                              </div>

                              <button
                                type="button"
                                className={`pep-configure-toggle ${capability.selected ? "is-on" : ""}`}
                                aria-pressed={capability.selected}
                                disabled={!capability.selectable || capability.locked}
                                onClick={() => toggleCapability(capability.slug, !capability.selected)}
                              >
                                <span className="visually-hidden">
                                  {capability.selected
                                    ? locale === "en"
                                      ? `Remove ${capability.title}`
                                      : `Retirer ${capability.title}`
                                    : locale === "en"
                                      ? `Add ${capability.title}`
                                      : `Ajouter ${capability.title}`}
                                </span>
                                {capability.selected ? "✓" : "+"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <p className="pep-configure-note mb-0">
                      {locale === "en"
                        ? "Additional pricing and availability details are confirmed in the next steps."
                        : "Les détails de tarification et de disponibilité sont confirmés aux étapes suivantes."}
                    </p>
                  </aside>
                </div>

                <div className="pep-configure-action-bar">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => changeStep(1)}>
                    {locale === "en" ? "Back" : "Retour"}
                  </button>
                  <div className="pep-configure-action-bar__trust">
                    <strong>{locale === "en" ? "Commercial truth first" : "Conforme à la réalité de l’offre"}</strong>
                    <span>
                      {locale === "en"
                        ? "Only available or honestly limited options are proposed."
                        : "Nous ne proposons que des options réellement disponibles ou honnêtement limitées."}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <span className="small text-secondary">{locale === "en" ? "Step 2 of 4" : "Étape 2 sur 4"}</span>
                    <button type="button" className="btn btn-primary" onClick={() => changeStep(3)}>
                      {locale === "en" ? "Continue with this solution" : "Continuer avec cette solution"}
                    </button>
                  </div>
                </div>
              </section>
            ) : step === 3 ? (
              <section className="pep-configure-panel">
                <div className="pep-configure-panel__header">
                  <h2>{locale === "en" ? "Your organization" : "Votre organisation"}</h2>
                  <p>
                    {locale === "en"
                      ? "Tell us the business volumes that matter for this scope."
                      : "Indiquez les volumes utiles à la qualification commerciale de votre périmètre."}
                  </p>
                </div>

                <div className="row g-3">
                  {dimensions.map((dimension) => {
                    const value = selection.quantities[dimension.key] ?? dimension.defaultQty;
                    const unknown = unknownQuantityKeys.has(dimension.key);
                    const lowWarning = !unknown && value < (dimension.confirmBelowQty ?? 0);
                    return (
                      <div className="col-md-6" key={dimension.key}>
                        <div className="pep-configure-field-card">
                          <label className="form-label fw-semibold" htmlFor={`cfg-${dimension.key}`}>
                            {dimensionLabel(dimension, locale)}
                          </label>
                          {dimensionHint(dimension, locale) ? (
                            <p className="small text-secondary mb-2">{dimensionHint(dimension, locale)}</p>
                          ) : null}
                          <input
                            id={`cfg-${dimension.key}`}
                            type="number"
                            className="form-control form-control-lg"
                            min={dimensionMinQty(dimension)}
                            value={unknown ? "" : value}
                            placeholder={unknown ? (locale === "en" ? "To be confirmed" : "À confirmer") : undefined}
                            disabled={unknown}
                            onChange={(event) => updateQuantity(dimension.key, Number(event.target.value || dimensionMinQty(dimension)))}
                          />
                          <div className="form-check mt-3">
                            <input
                              id={`unknown-${dimension.key}`}
                              type="checkbox"
                              className="form-check-input"
                              checked={unknown}
                              onChange={(event) => toggleUnknownQuantity(dimension.key, event.target.checked)}
                            />
                            <label className="form-check-label" htmlFor={`unknown-${dimension.key}`}>
                              {locale === "en" ? "I do not know this volume yet" : "Je ne connais pas encore ce volume"}
                            </label>
                          </div>
                          {lowWarning && dimension.confirmBelowQty ? (
                            <div className="small text-warning mt-2">
                              {lowQuantityMessage(dimension, value, locale)}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="pep-configure-action-bar">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => changeStep(2)}>
                    {locale === "en" ? "Back" : "Retour"}
                  </button>
                  <div className="d-flex align-items-center gap-3">
                    <span className="small text-secondary">{locale === "en" ? "Step 3 of 4" : "Étape 3 sur 4"}</span>
                    <button type="button" className="btn btn-primary" onClick={() => changeStep(4)}>
                      {locale === "en" ? "Continue to configuration" : "Continuer vers la configuration"}
                    </button>
                  </div>
                </div>
              </section>
            ) : (
              <section className="pep-configure-panel">
                <div className="pep-configure-panel__header">
                  <h2>{locale === "en" ? "Your configuration" : "Votre configuration"}</h2>
                  <p>
                    {locale === "en"
                      ? "Review the proposed scope and send it to our team."
                      : "Relisez le périmètre proposé et transmettez-le à notre équipe."}
                  </p>
                </div>

                <div className="pep-configure-summary-grid">
                  <div className="pep-configure-summary-card">
                    <h3>{locale === "en" ? "Configuration summary" : "Récapitulatif"}</h3>
                    <ul className="list-unstyled mb-0">
                      <li>
                        <strong>{locale === "en" ? "Context" : "Contexte"}:</strong>{" "}
                        {locale === "en" ? context.titleEn : context.titleFr}
                      </li>
                      <li>
                        <strong>{locale === "en" ? "Main solution" : "Solution principale"}:</strong> {primary?.title}
                      </li>
                      {selectedCapabilityTitles.length > 0 ? (
                        <li>
                          <strong>{locale === "en" ? "Complementary capabilities" : "Capacités complémentaires"}:</strong>{" "}
                          {selectedCapabilityTitles.join(", ")}
                        </li>
                      ) : null}
                    </ul>
                  </div>

                  <div className="pep-configure-summary-card">
                    <h3>{locale === "en" ? "Commercial estimate" : "Projection commerciale"}</h3>
                    <div className="pep-configure-estimate">{estimate?.formatted}</div>
                    <p className="text-secondary mb-0">{estimate?.note}</p>
                  </div>
                </div>

                <div className="row g-4 mt-1">
                  <div className="col-lg-6">
                    <div className="pep-configure-summary-card h-100">
                      <h3>{locale === "en" ? "Organization details" : "Volumes organisation"}</h3>
                      <ul className="list-unstyled mb-0">
                        {dimensions.map((dimension) => (
                          <li key={dimension.key} className="d-flex justify-content-between gap-3 py-2 border-bottom">
                            <span>{dimensionLabel(dimension, locale)}</span>
                            <strong>
                              {unknownQuantityKeys.has(dimension.key)
                                ? locale === "en"
                                  ? "To be confirmed"
                                  : "À confirmer"
                                : selection.quantities[dimension.key] ?? dimension.defaultQty}
                            </strong>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <form className="pep-configure-summary-card h-100" onSubmit={onSubmit}>
                      <h3>{finalCta.title}</h3>
                      {finalCta.subtitle ? (
                        <p className="text-secondary pep-configure-final-cta-subtitle">
                          {finalCta.subtitle}
                        </p>
                      ) : null}
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label">{locale === "en" ? "Company" : "Entreprise"} *</label>
                          <input className="form-control" value={company} onChange={(event) => setCompany(event.target.value)} required />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">{locale === "en" ? "Contact name" : "Nom du contact"} *</label>
                          <input className="form-control" value={name} onChange={(event) => setName(event.target.value)} required />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Email *</label>
                          <input type="email" className="form-control" value={email} onChange={(event) => setEmail(event.target.value)} required />
                        </div>
                        <div className="col-12">
                          <label className="form-label">{locale === "en" ? "Phone" : "Téléphone"} *</label>
                          <input type="tel" className="form-control" value={phone} onChange={(event) => setPhone(event.target.value)} required minLength={8} />
                        </div>
                      </div>
                      {error ? (
                        <div className="pep-configure-failure alert alert-danger mt-3 mb-0" role="alert">
                          <p className="mb-2">{error}</p>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            disabled={submitPending}
                            onClick={() => void sendSubmission()}
                          >
                            {submitPending
                              ? locale === "en"
                                ? "Sending…"
                                : "Envoi…"
                              : locale === "en"
                                ? "Retry sending"
                                : "Réessayer l’envoi"}
                          </button>
                        </div>
                      ) : null}
                      <div className="pep-configure-form-actions">
                        <button type="button" className="btn btn-outline-secondary" onClick={() => changeStep(3)}>
                          {locale === "en" ? "Back" : "Retour"}
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={submitPending}
                          data-cta-mode={finalCta.mode}
                        >
                          {submitPending
                            ? locale === "en"
                              ? "Sending…"
                              : "Envoi…"
                            : finalCta.submitLabel}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </SiteLayout>
  );
}
